<template>
  <div class="matchmaker-list-page">
    <div class="page-hero" v-if="isMatchmaker">
      <div class="hero-top">
        <button class="hero-back" @click="$router.go(-1)">
          <img src="../assets/back.png" alt="back" />
        </button>
        <div class="hero-actions">
          <button class="hero-action" @click="scoreClick">
            <van-icon name="points" />
            <span>我的积分</span>
          </button>
          <button class="hero-action" @click="$router.push({ path: '/matchMakerInfo' })">
            <van-icon name="edit" />
            <span>编辑资料</span>
          </button>
        </div>
      </div>
    </div>
    <div class="matchmaker-top">
      <div class="top-title">红娘团队</div>
      <div class="top-desc">按跟进数量优先展示优质红娘</div>
    </div>

    <div class="stats-grid">
      <div class="stat-item">
        <div class="stat-number">{{ totalCount }}</div>
        <div class="stat-label">红娘数量</div>
      </div>
      <div class="stat-item">
        <div class="stat-number">{{ totalSuccessCount }}</div>
        <div class="stat-label">当前展示牵线</div>
      </div>
      <div class="stat-item">
        <div class="stat-number">{{ highestPoint }}</div>
        <div class="stat-label">当前展示最高积分</div>
      </div>
    </div>

    <div class="search-panel">
      <van-field v-model="searchName" placeholder="搜索红娘姓名" @keyup.enter="handleQuery">
        <template #left-icon>
          <van-icon name="search" />
        </template>
      </van-field>
      <van-field v-model="workAreaText" placeholder="工作区域" readonly clickable @click="showWorkAreaPicker = true">
        <template #right-icon>
          <van-icon name="arrow-down" />
        </template>
      </van-field>
      <div class="filter-buttons">
        <button class="secondary-btn" @click="handleClear">清空</button>
        <button class="primary-btn" @click="handleQuery">查询</button>
      </div>
    </div>

    <div class="matchmaker-list-content">
      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <van-list v-model="loading" :finished="finished" finished-text="没有更多了" @load="onLoad">
          <div class="matchmaker-list">
            <div v-for="item in matchmakerList" :key="item.id" class="matchmaker-card" @click="goToDetail(item.id)">
              <div class="card-head">
                <div class="card-avatar">
                  <img :src="item.avatarLink || defaultPhoto" alt="" @error="handleImageError($event)" />
                </div>
                <div class="card-main">
                  <div class="card-title-row">
                    <span class="card-name">{{ item.name }}</span>
                    <span class="card-badge">{{ getLevelText(item) }}</span>
                  </div>
                  <div class="card-score">积分：{{ item.points || 0 }} · 部门：{{ item.department || '未填写' }}</div>
                </div>
                <div class="card-favorite" v-if="role === 'Member'">
                  <van-icon :name="favoriteMap[item.id] ? 'star' : 'star-o'"
                    :class="{ 'is-favorite': favoriteMap[item.id] }" @click.stop="toggleFavorite(item)" />
                </div>
              </div>

              <div class="card-stats">
                <div class="card-stat">
                  <van-icon name="location-o" />
                  <span>{{ getWorkAreaName(item.workArea) || '未填写' }}</span>
                </div>
                <div class="card-stat">
                  <van-icon name="passed" />
                  <span>{{ item.successesQuantity || 0 }} 对</span>
                </div>
                <div class="card-stat">
                  <van-icon name="friends-o" />
                  <span>{{ item.followUpQuantity || 0 }} 人跟进</span>
                </div>
                <div class="card-stat">
                  <van-icon name="star-o" />
                  <span>{{ item.points || 0 }} 积分</span>
                </div>
              </div>

              <div class="card-actions">
                <button class="card-btn outline" @click.stop="toggleFavorite(item)" v-if="role === 'Member'">
                  {{ favoriteMap[item.id] ? '取消收藏' : '收藏红娘' }}
                </button>
                <button class="card-btn primary" @click.stop="goToDetail(item.id)">
                  查看详情
                </button>
              </div>
            </div>
          </div>
        </van-list>
      </van-pull-refresh>
    </div>

    <van-popup v-model="showWorkAreaPicker" position="bottom">
      <van-picker title="选择工作区域" :columns="workAreaColumns" @confirm="onWorkAreaConfirm"
        @cancel="showWorkAreaPicker = false" show-toolbar />
    </van-popup>

    <TabBar active-key="matchmaker" />
  </div>
</template>

<script>
import TabBar from "@/components/TabBar.vue";
import { Toast } from "vant";
import { getMatchmakerInfoPage } from "@/api/matchmakerInfo";
import { addFavorite, removeFavorite, getFavoriteList } from "@/api/matchmakerFavorite";

export default {
  components: { TabBar },
  data() {
    return {
      matchmakerList: [],
      favoriteMap: {},
      searchName: "",
      workArea: null,
      workAreaText: "",
      showWorkAreaPicker: false,
      workAreaColumns: [
        { text: "全部", value: null },
        { text: "W区", value: 1 },
        { text: "E区", value: 2 },
        { text: "M区", value: 3 },
        { text: "R区", value: 4 },
        { text: "东莞", value: 15 }
      ],
      loading: false,
      finished: false,
      refreshing: false,
      page: 1,
      pageSize: 10,
      totalCount: 0,
      defaultPhoto: require("../assets/defaultPhoto.png")
    };
  },
  computed: {
    baseInfo() {
      return this.$store.getters.baseInfo;
    },
    role() {
      return this.$store.getters.role;
    },
    isMatchmaker() {
      return this.$store.getters.role == 'Matchmaker'
    },
    totalSuccessCount() {
      return this.matchmakerList.reduce((sum, item) => sum + (item.successesQuantity || 0), 0);
    },
    highestPoint() {
      if (!this.matchmakerList.length) return 0;
      return Math.max.apply(null, this.matchmakerList.map(item => item.points || 0));
    }
  },
  methods: {
    getLevelText(item) {
      const point = item.points || 0;
      if (point >= 3000) return "👑 金牌";
      if (point >= 2000) return "💎 资深";
      return "🌟 优秀";
    },
    getWorkAreaName(value) {
      if (!value) return "";
      const item = this.workAreaColumns.find(col => col.value === value);
      return item ? item.text : "";
    },
    handleImageError(event) {
      event.target.src = this.defaultPhoto;
    },
    onWorkAreaConfirm(params) {
      let selectedOptions = params.selectedOptions;
      let value = params.value;
      if (!selectedOptions || selectedOptions.length === 0) {
        if (value !== undefined && value !== null) {
          const item = this.workAreaColumns.find(col => col.value === value);
          this.workArea = value;
          this.workAreaText = item ? item.text : String(value);
        }
      } else {
        this.workArea = selectedOptions[0].value;
        this.workAreaText = selectedOptions[0].text;
      }
      this.showWorkAreaPicker = false;
    },
    handleClear() {
      this.searchName = "";
      this.workArea = null;
      this.workAreaText = "";
      this.page = 1;
      this.finished = false;
      this.matchmakerList = [];
      this.loadData();
    },
    handleQuery() {
      this.page = 1;
      this.finished = false;
      this.matchmakerList = [];
      this.loading = true;
      this.loadData();
    },
    async onLoad() {
      await this.loadData();
    },
    async onRefresh() {
      this.page = 1;
      this.matchmakerList = [];
      this.finished = false;
      this.loading = true;
      await this.loadData();
      this.refreshing = false;
    },
    async loadData() {
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          approvalStatus: 2
        };
        if (this.searchName) {
          params.name = this.searchName;
        }
        if (this.workArea) {
          params.workArea = this.workArea;
        }

        const res = await getMatchmakerInfoPage(params);
        if (res.code === 20000 && res.data) {
          const pageData = res.data.items || [];
          this.totalCount = res.data.total || 0;
          this.matchmakerList.push(...pageData);
          if (pageData.length < this.pageSize || this.matchmakerList.length >= this.totalCount) {
            this.finished = true;
          } else {
            this.page++;
          }
        } else {
          this.finished = true;
        }
        this.loadFavoriteStatus();
      } catch (error) {
        Toast.fail({ message: "加载失败" });
        this.finished = true;
      } finally {
        this.loading = false;
      }
    },
    async loadFavoriteStatus() {
      if (this.role !== "Member" || !this.baseInfo || !this.baseInfo.id) return;
      try {
        const res = await getFavoriteList(this.baseInfo.id);
        if (res.code === 20000 && res.data) {
          const map = {};
          res.data.forEach(id => {
            map[id] = true;
          });
          this.favoriteMap = map;
        }
      } catch (error) {
        console.error("加载收藏状态失败", error);
      }
    },
    async toggleFavorite(item) {
      if (this.role !== "Member") {
        Toast.fail({ message: "只有会员才能收藏红娘" });
        return;
      }
      if (!this.baseInfo || !this.baseInfo.id) {
        Toast.fail({ message: "请先登录" });
        return;
      }
      const isFavorite = this.favoriteMap[item.id];
      try {
        if (isFavorite) {
          const res = await removeFavorite(this.baseInfo.id, item.id);
          if (res.code === 20000) {
            this.$set(this.favoriteMap, item.id, false);
            Toast.success({ message: "已取消收藏" });
          }
        } else {
          const res = await addFavorite(this.baseInfo.id, item.id);
          if (res.code === 20000) {
            this.$set(this.favoriteMap, item.id, true);
            Toast.success({ message: "收藏成功" });
          }
        }
      } catch (error) {
        Toast.fail({ message: "操作失败" });
      }
    },
    goToDetail(id) {
      this.$router.push({ path: `/excellentMatchmaker/${id}` });
    },
    saveScrollState() {
      try {
        const state = {
          scrollTop: document.documentElement.scrollTop || document.body.scrollTop || 0,
          matchmakerList: this.matchmakerList,
          page: this.page,
          finished: this.finished,
          totalCount: this.totalCount
        };
        sessionStorage.setItem("matchmakerList_scroll_state", JSON.stringify(state));
      } catch (e) {
        // ignore
      }
    },
    restoreScrollState() {
      try {
        const raw = sessionStorage.getItem("matchmakerList_scroll_state");
        if (raw) {
          const state = JSON.parse(raw);
          this.matchmakerList = state.matchmakerList || [];
          this.page = state.page || 1;
          this.finished = state.finished || false;
          this.totalCount = state.totalCount || 0;
          this.loading = false;
          this.refreshing = false;
          this.$nextTick(() => {
            window.scrollTo(0, state.scrollTop || 0);
          });
          sessionStorage.removeItem("matchmakerList_scroll_state");
        }
      } catch (e) {
        // ignore
      }
    },
    scoreClick() {
      this.activeState = "score";
      this.$router.push({ path: "/myScore" });
    }
  },
  // activated() {
  //   this.restoreScrollState();
  // },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.restoreScrollState();
    });
  },
  beforeRouteLeave(to, from, next) {
    this.saveScrollState();
    next();
  }
};
</script>

<style lang="less" scoped src="../style/matchmakerList.less" />



// WEBPACK FOOTER //
// src/views/matchmakerList.vue